import React from 'react';
import ReactModal from 'react-modal';
import PropTypes from 'prop-types';
import { defaults, omit } from 'lodash';

import Logger from '@sm360/logger-sentry';

import { ConfigsService, WindowProxy } from '@sm360/widget-commons-core-services';

import ModalService from 'commons-general/services/ModalService/ModalService.js';
import 'commons-general/components/Modal/Modal.scss';

function filterProps(object) {
    return omit(object, ['isOpen', 'parentSelector', 'isScrollable']);
}

/**
 * ModalWrapper is a component providing a way to use modals.
 * It is a class component extending a react pure component.
 * It's wrapping a {@link ReactModal} component and passing its children to it.
 *
 * @extends React.PureComponent
 * @see https://reactcommunity.org/react-modal/#general-usage
 * @see https://reactjs.org/docs/react-api.html#reactpurecomponent
 * @since 1.8.1
 */
class ModalWrapper extends React.PureComponent {
    constructor(props) {
        super(props);

        this.state = {
            isOpen: false,
            scrollPosition: 0,
            classNames: ['sr-modal__wrapper'],
        };

        this.callbacks = {
            onChange: this.handleChange.bind(this),
            onClose: this.onClose.bind(this),
        };

        this.modal = ModalService.get(this.props.name); // eslint-disable-line react/prop-types

        this.handleChange = this.handleChange.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.onOpen = this.onOpen.bind(this);

        this.getModalParent = this.getModalParent.bind(this);
        this.getModalParentScrollPosition = this.getModalParentScrollPosition.bind(this);
        this.setScrollEventListener = this.setScrollEventListener.bind(this);
        this.scrollInit = this.scrollInit.bind(this);
        this.logger = new Logger(this.constructor.displayName, ConfigsService.getLoggerContext());
    }

    componentWillMount() {
        if (WindowProxy.get()) {
            ReactModal.setAppElement('body');
        }
    }

    componentDidMount() {
        this.modal.addCallbacks(this.callbacks);

        if (this.props.isScrollable) {
            this.state.classNames.push('is-scrollable');
        }
    }

    componentWillUnmount() {
        this.modal.removeCallbacks(this.callbacks);
    }

    onClose() {
        const windowProxy = WindowProxy.get();
        if (windowProxy) {
            const pageYOffset = windowProxy.pageYOffset || 0;
            const { scrollPosition } = this.state;
            if (scrollPosition !== pageYOffset) {
                windowProxy.scrollTo(0, scrollPosition);
            }
        }
        return true;
    }

    onOpen() {
        const windowProxy = WindowProxy.get();
        if (windowProxy) {
            this.scrollInit(windowProxy);
            this.getModalParentScrollPosition(windowProxy);
        }
    }

    getModalParent() {
        const windowProxy = WindowProxy.get();
        if (windowProxy) {
            const parent = windowProxy.document.getElementsByClassName('widget-sr');
            if (parent && parent.length) {
                return parent[0];
            }

            this.logger.error(new Error('ModalWrapper parent must be ".widget-sr"'));
            return windowProxy.document.getElementsByTagName('body')[0];
        }

        return null;
    }

    getModalParentScrollPosition(windowProxy) {
        const scrollPosition = windowProxy.pageYOffset || 0;
        if (scrollPosition) {
            this.setState({ scrollPosition });
        }
    }

    setScrollEventListener(el) {
        el.scrollTop = 1;
        el.addEventListener('scroll', this.handleScroll);
    }

    scrollInit(windowProxy) {
        const whiteModalWrapper = windowProxy.document.getElementsByClassName('sr-modal__wrapper is-scrollable');
        const modalWrapper = windowProxy.document.getElementsByClassName('sr-modal__wrapper');

        if (whiteModalWrapper && whiteModalWrapper[0]) {
            this.setScrollEventListener(whiteModalWrapper[0]);
        } else if (modalWrapper && modalWrapper[0]) {
            const scrollable = modalWrapper[0].querySelectorAll('.is-scrollable');
            if (scrollable && scrollable[0]) {
                this.setScrollEventListener(scrollable[0]);
            }
        }
    }

    handleChange(value) {
        this.setState({ ...value });
    }
    handleScroll(event) {
        const { scrollTop, offsetHeight, scrollHeight } = event.target;
        const scrolledHeight = scrollTop + offsetHeight;
        if (scrollTop <= 0) {
            event.target.scrollTop = 1;
        } else if (scrolledHeight >= scrollHeight) {
            // TODO : voir si on peut ameliorer en utilisant la valeur max (scrollHeight) - Bug iOS safari only
            event.target.scrollTop -= 1;
        }
    }

    render() {
        const {
            name, // eslint-disable-line react/prop-types
            children, // eslint-disable-line react/prop-types
            ...options
        } = this.props;

        const fallbacks = {
            contentLabel: name,
            onRequestClose: this.modal.close,
            onAfterOpen: this.onOpen,
        };

        const properties = defaults({}, options, fallbacks);

        return (
            <ReactModal {...properties} isOpen={this.state.isOpen} parentSelector={this.getModalParent}>
                <div className={this.state.classNames.join(' ')}>
                    {/* Here we clone children add pass them new props registered into ModalInstance */
                    React.Children.map(children, (child) => React.cloneElement(child, { ...this.state.properties }))}
                </div>
            </ReactModal>
        );
    }
}

ModalWrapper.defaultProps = defaults(
    {},
    {
        children: null,
        isScrollable: false,
    },
    filterProps(ReactModal.defaultProps)
);

ModalWrapper.propTypes = defaults(
    {},
    {
        name: PropTypes.string.isRequired,
        children: PropTypes.node,
        isScrollable: PropTypes.bool,
    },
    filterProps(ReactModal.propTypes)
);

ModalWrapper.propTypes = {
    isScrollable: PropTypes.bool,
};

ModalWrapper.displayName = 'ModalWrapper';

export { filterProps };
export default ModalWrapper;



// WEBPACK FOOTER //
// ./src/components/Modal/Modal.jsx